/**
 * The IT Admin area (5 Oct): User lookup (`/it-admin`, and a person's profile
 * under it) and IT training (`/it-admin/training`, lessons for IT staff;
 * `/it-admin/training/:slug`, one lesson). Ticket search (21 Oct,
 * `/it-admin/ticket-search`) needs `/auth/me` → `ticket_search`.
 *
 * For HelpDesk admins and IT teams (`/auth/me` → `it_admin`); the server
 * refuses everyone else (403) and the whole area 404s while
 * `IT_ADMIN_ENABLED` is off. There is no sub-navigation since 7 Oct: the
 * header's "IT Admin ▾" menu opens each page directly.
 */

import { Alert } from "@norican/react";
import { Outlet, useLocation } from "react-router";

import { LoadingState, PageContainer } from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import { useMe } from "@/lib/api";

export function ItAdminLayout() {
  const { t } = useT("tickets");
  const { pathname } = useLocation();
  const section = pathname.startsWith("/it-admin/training")
    ? "training"
    : pathname.startsWith("/it-admin/assign")
      ? "assign"
      : pathname.startsWith("/it-admin/routing")
        ? "routing"
        : pathname.startsWith("/it-admin/email-intake")
          ? "emailIntake"
          : pathname.startsWith("/it-admin/ticket-search")
            ? "ticketSearch"
            : "lookup";
  useDocumentTitle(t(`itAdmin.section.${section}`));
  const { data: me, isPending } = useMe();

  if (isPending) {
    return (
      <PageContainer title={t("itAdmin.title")}>
        <LoadingState />
      </PageContainer>
    );
  }
  const allowed =
    section === "assign" || section === "routing"
      ? me?.assign_suggest === true
      : section === "emailIntake"
        ? me?.email_intake === true
        : section === "ticketSearch"
          ? me?.ticket_search === true
          : me?.it_admin === true;
  if (!allowed) {
    return (
      <PageContainer title={t("itAdmin.title")}>
        <Alert tone="neutral">{t("itAdmin.notAllowed")}</Alert>
      </PageContainer>
    );
  }
  return (
    <PageContainer
      title={t(`itAdmin.section.${section}`)}
      width={section === "ticketSearch" ? "screen" : "default"}
    >
      <Outlet />
    </PageContainer>
  );
}
